// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import React, { useState, useEffect } from 'react';
import Button from './Button';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { t } from '../translations';
import CustomerRegistration from './CustomerRegistration';
import BakerRegistration from './BakerRegistration';
import { getCurrentBakeryId, getBakery } from '../utils/bakeryUtils';
import { validateEmail, validateRequired } from '../utils/formValidationUtils';
import InputField from './forms/InputField';
import PasswordField from './forms/PasswordField';
import { Mail, KeyRound, CheckCircle } from 'lucide-react';


export default function AuthScreen({ isModal = false, onClose = null, setView = null, allowBakerRegistration = false, view = null }) {
  const { language } = useLanguage();
  const { showToast } = useToast();
  const [isLogin, setIsLogin] = useState(view !== 'signup-baker');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [registrationMode, setRegistrationMode] = useState(null);
  const [bakeryName, setBakeryName] = useState('');

  // Form validation errors
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [resetEmailError, setResetEmailError] = useState('');

  useEffect(() => {
    const loadBakeryName = async () => {
      const bakeryId = getCurrentBakeryId();
      if (bakeryId) {
        try {
          const bakery = await getBakery(bakeryId);
          if (bakery && bakery.name) {
            setBakeryName(bakery.name);
          }
        } catch (error) {
          console.error('Error loading bakery name:', error);
        }
      }
    };

    loadBakeryName();
  }, []);

  const handleLogin = async () => {
    setError('');
    setEmailError('');
    setPasswordError('');

    // Validate email
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setEmailError(emailValidation.message);
      return;
    }

    // Validate password
    const passwordValidation = validateRequired(password, 'Password');
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.message);
      return;
    }

    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      showToast(t('loginSuccess', language) || 'Login successful!', 'success');
      if (isModal && onClose) {
        setTimeout(() => onClose(), 500);
      }
      // Don't call setView here - let App.js handle navigation based on user role
    } catch (err) {
      const errorMessage = err.code === 'auth/invalid-credential'
        ? (language === 'fr' ? 'Email ou mot de passe incorrect' : 'Invalid email or password')
        : err.message;
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setResetEmailError('');
    setResetMessage('');

    // Validate email
    const emailValidation = validateEmail(resetEmail);
    if (!emailValidation.isValid) {
      setResetEmailError(emailValidation.message);
      setResetMessage(emailValidation.message);
      return;
    }

    try {
      // Use custom action URL that redirects to our branded page
      const actionCodeSettings = {
        url: `${window.location.origin}/reset-password`,
        handleCodeInApp: false, // Don't handle in app, redirect to web
      };

      await sendPasswordResetEmail(auth, resetEmail, actionCodeSettings);
      setEmailSent(true);
      const successMsg = language === 'fr'
        ? 'Email de récupération envoyé! Vérifiez votre boîte mail et vos spams.'
        : 'Password reset email sent! Check your inbox and spam folder.';
      setResetMessage(successMsg);
      showToast(successMsg, 'success');
    } catch (err) {
      const errorMsg = err.message;
      setResetMessage(errorMsg);
      setResetEmailError(errorMsg);
      setEmailSent(false);
      showToast(errorMsg, 'error');
    }
  };

  // Allow Enter key to submit form
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleLogin();
    }
  };

  const containerClass = isModal
    ? "bg-warm-white rounded-3xl shadow-lifted p-8 w-full border border-charcoal/10"
    : "min-h-screen bg-paper-texture flex";

  const innerClass = isModal
    ? ""
    : "bg-warm-white rounded-3xl shadow-lifted p-8 lg:p-12 w-full max-w-md border border-charcoal/10";

  // Show forgot password screen
  if (showForgotPassword) {
    return (
      <div className={containerClass}>
        {/* Left Side - Hero Section (only on non-modal, non-bakery reset) */}
        {!isModal && !bakeryName && (
          <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12">
            <div className="max-w-xl">
              <div className="inline-block p-6 bg-gradient-to-br from-cream to-cream-dark rounded-3xl shadow-soft-lg mb-8">
                <img
                  src="/digibakery-logo.svg"
                  alt="Digi-Bakery"
                  className="h-16 w-auto mx-auto"
                  style={{
                    filter: 'invert(54%) sepia(32%) saturate(727%) hue-rotate(332deg) brightness(91%) contrast(85%)'
                  }}
                />
              </div>

              <h1 className="font-display text-5xl font-bold text-charcoal mb-6 tracking-tight leading-tight">
                <span className="text-terracotta">Digi-Bakery</span>
                <br />
                <span className="text-3xl font-display text-charcoal-light">
                  {language === 'fr'
                    ? 'Récupération de mot de passe'
                    : 'Password Recovery'
                  }
                </span>
              </h1>

              <p className="font-body-serif text-lg text-charcoal-light leading-relaxed">
                {language === 'fr'
                  ? 'Récupérez l\'accès à votre compte en quelques étapes simples.'
                  : 'Regain access to your account in a few simple steps.'
                }
              </p>
            </div>
          </div>
        )}

        {/* Right Side - Password Reset Form */}
        <div className={`${isModal ? '' : 'w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-12'}`}>
          <div className={innerClass}>
            {/* Mobile Logo - only show on small screens when not in modal and no bakery name */}
            {!isModal && !bakeryName && (
              <div className="lg:hidden text-center mb-8">
                <div className="inline-block p-6 bg-gradient-to-br from-cream to-cream-dark rounded-3xl shadow-soft-lg mb-4">
                  <img
                    src="/digibakery-logo.svg"
                    alt="Digi-Bakery"
                    className="h-12 w-auto mx-auto"
                    style={{
                      filter: 'invert(54%) sepia(32%) saturate(727%) hue-rotate(332deg) brightness(91%) contrast(85%)'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Bakery-specific logo and name */}
            {bakeryName && (
              <div className="text-center mb-8">
                <div className="inline-block p-6 bg-gradient-to-br from-cream to-cream-dark rounded-3xl shadow-soft-lg mb-6">
                  <img
                    src="/digibakery-logo.svg"
                    alt="Digi-Bakery"
                    className="h-12 w-auto mx-auto"
                    style={{
                      filter: 'invert(54%) sepia(32%) saturate(727%) hue-rotate(332deg) brightness(91%) contrast(85%)'
                    }}
                  />
                </div>
                <h1 className="font-display text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">{bakeryName}</h1>
                <p className="font-body-serif text-charcoal-light mt-4 text-lg italic">
                  {language === 'fr' ? 'Récupération de mot de passe' : 'Password Recovery'}
                </p>
              </div>
            )}

            {/* Generic reset title when no bakery */}
            {!bakeryName && (
              <div className="text-center mb-8">
                <div className="inline-block p-6 bg-gradient-to-br from-cream to-cream-dark rounded-3xl shadow-soft-lg mb-6">
                  <KeyRound size={48} className="text-terracotta mx-auto" strokeWidth={2} />
                </div>
                <h2 className="font-display text-2xl lg:text-3xl font-bold text-charcoal tracking-tight">
                  {language === 'fr' ? 'Récupération de mot de passe' : 'Reset Password'}
                </h2>
                <p className="font-body-serif text-charcoal-light mt-4 text-base italic">
                  {language === 'fr'
                    ? 'Entrez votre email pour recevoir un lien de récupération'
                    : 'Enter your email to receive a recovery link'
                  }
                </p>
              </div>
            )}

            {resetMessage && (
              <div className={`mb-6 p-4 rounded-xl text-sm border-l-4 ${
                emailSent
                  ? 'bg-gradient-to-r from-sage-light/20 to-cream border-sage-dark'
                  : 'bg-gradient-to-r from-red-50 to-cream border-red-600'
              }`}>
                <div className="flex items-center gap-3">
                  {emailSent ? (
                    <CheckCircle size={24} className="text-sage-dark" strokeWidth={2} />
                  ) : (
                    <span className="text-2xl">⚠️</span>
                  )}
                  <div>
                    <div className="font-ui font-bold text-charcoal">
                      {emailSent ? (language === 'fr' ? 'Email envoyé!' : 'Email sent!') : (language === 'fr' ? 'Erreur' : 'Error')}
                    </div>
                    <div className="font-body-serif text-charcoal-light mt-1">{resetMessage}</div>
                  </div>
                </div>
              </div>
            )}

            {!emailSent ? (
              <div className="space-y-6">
                <InputField
                  label={t('email', language)}
                  type="email"
                  value={resetEmail}
                  onChange={(e) => {
                    setResetEmail(e.target.value);
                    setResetEmailError('');
                    setResetMessage('');
                    setEmailSent(false);
                  }}
                  onBlur={() => {
                    if (resetEmail) {
                      const validation = validateEmail(resetEmail);
                      if (!validation.isValid) {
                        setResetEmailError(validation.message);
                      }
                    }
                  }}
                  error={resetEmailError}
                  icon={Mail}
                  placeholder="your@email.com"
                  required
                  autoComplete="email"
                />

                <Button
                  onClick={handlePasswordReset}
                  variant="primary" className="w-full py-4 font-bold"
                >
                  {language === 'fr' ? 'Envoyer le lien de récupération' : 'Send Recovery Link'}
                </Button>

                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetMessage('');
                    setResetEmail('');
                    setEmailSent(false);
                  }}
                  className="w-full text-sm py-2 rounded-xl text-charcoal-light hover:text-charcoal font-ui transition-colors"
                >
                  {t('backToLogin', language) || 'Back to Login'}
                </button>
              </div>
            ) : (
              <div className="space-y-6 text-center">
                <div className="inline-block p-6 bg-gradient-to-br from-sage-light/30 to-cream rounded-3xl shadow-soft-lg">
                  <Mail size={48} className="text-sage-dark mx-auto" strokeWidth={2} />
                </div>

                <div>
                  <h3 className="font-display text-xl font-bold text-charcoal mb-2">
                    {language === 'fr' ? 'Vérifiez votre boîte mail' : 'Check your email'}
                  </h3>
                  <p className="font-body-serif text-charcoal-light text-sm leading-relaxed">
                    {language === 'fr'
                      ? `Nous avons envoyé un lien de récupération à ${resetEmail}. Cliquez sur le lien dans l'email pour réinitialiser votre mot de passe.`
                      : `We've sent a recovery link to ${resetEmail}. Click the link in the email to reset your password.`
                    }
                  </p>
                </div>

                <div className="space-y-3">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setResetEmail('');
                      setEmailSent(false);
                      setResetMessage('');
                    }}
                    className="w-full py-2 rounded-xl text-sm font-ui"
                  >
                    {language === 'fr' ? 'Changer d\'adresse email' : 'Change email address'}
                  </Button>

                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetMessage('');
                      setResetEmail('');
                      setEmailSent(false);
                    }}
                    className="w-full text-sm py-2 rounded-xl text-charcoal-light hover:text-charcoal font-ui transition-colors"
                  >
                    {t('backToLogin', language) || 'Back to Login'}
                  </button>
                </div>

                <div className="pt-4 border-t border-charcoal/10">
                  <p className="font-ui text-xs text-charcoal-light mb-2">
                    {language === 'fr' ? 'Vous ne recevez pas l\'email?' : 'Not receiving the email?'}
                  </p>
                  <div className="space-y-1 font-body-serif text-xs text-charcoal-light">
                    <p>• {language === 'fr' ? 'Vérifiez votre dossier spam' : 'Check your spam folder'}</p>
                    <p>• {language === 'fr' ? 'Attendez quelques minutes' : 'Wait a few minutes'}</p>
                    <p>• {language === 'fr' ? 'Vérifiez l\'adresse email' : 'Verify the email address'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show customer registration directly (default)
  if (!isLogin && !allowBakerRegistration) {
    return (
      <CustomerRegistration
        onSuccess={() => {
          if (isModal && onClose) {
            setTimeout(() => onClose(), 500);
          }
        }}
        onSwitchToBaker={() => {
          // Bakery registration is now hidden - could be accessed via special URL or admin approval
          alert('Bakery registration is currently invite-only. Please contact support for access.\n\nFor development/testing, you can access it at: /?register=baker');
        }}
      />
    );
  }

  // Show bakery registration directly if bakery registration is allowed
  if (!isLogin && allowBakerRegistration) {
    return (
      <BakerRegistration
        onSuccess={() => {
          if (isModal && onClose) {
            setTimeout(() => onClose(), 500);
          }
        }}
        onSwitchToCustomer={() => {
          // For bakery registration mode, just go back to login
          setIsLogin(true);
          setRegistrationMode(null);
        }}
      />
    );
  }

  // Show baker registration if allowed (for admin access)
  if (allowBakerRegistration && registrationMode === 'baker') {
    return (
      <BakerRegistration
        onSuccess={() => {
          if (isModal && onClose) {
            setTimeout(() => onClose(), 500);
          }
        }}
        onSwitchToCustomer={() => setRegistrationMode('customer')}
      />
    );
  }

  // Show login screen
  return (
    <div className={containerClass}>
      {/* Left Side - Hero Section (only on non-modal, non-bakery login) */}
      {!isModal && !bakeryName && (
        <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12">
          <div className="max-w-xl">
            <div className="inline-block p-6 bg-gradient-to-br from-cream to-cream-dark rounded-3xl shadow-soft-lg mb-8">
              <img
                src="/digibakery-logo.svg"
                alt="Digi-Bakery"
                className="h-16 w-auto mx-auto"
                style={{
                  filter: 'invert(54%) sepia(32%) saturate(727%) hue-rotate(332deg) brightness(91%) contrast(85%)'
                }}
              />
            </div>

            <h1 className="font-display text-5xl font-bold text-charcoal mb-6 tracking-tight leading-tight">
              <span className="text-terracotta">Digi-Bakery</span>
              <br />
              <span className="text-3xl font-display text-charcoal-light">
                {language === 'fr'
                  ? 'La plateforme numérique pour boulangers'
                  : 'The Digital Platform for Bakers'
                }
              </span>
            </h1>

            <p className="font-body-serif text-lg text-charcoal-light leading-relaxed">
              {language === 'fr'
                ? 'Créez votre boutique en ligne, gérez vos commandes, organisez votre production et développez votre clientèle avec notre plateforme tout-en-un conçue spécialement pour les artisans boulangers.'
                : 'Create your online shop, manage orders, organize production, and grow your customer base with our all-in-one platform designed specifically for artisan bakers.'
              }
            </p>
          </div>
        </div>
      )}

      {/* Right Side - Login Form */}
      <div className={`${isModal ? '' : 'w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-12'}`}>
        <div className={innerClass}>
          {/* Mobile Logo - only show on small screens when not in modal and no bakery name */}
          {!isModal && !bakeryName && (
            <div className="lg:hidden text-center mb-8">
              <div className="inline-block p-6 bg-gradient-to-br from-cream to-cream-dark rounded-3xl shadow-soft-lg mb-4">
                <img
                  src="/digibakery-logo.svg"
                  alt="Digi-Bakery"
                  className="h-12 w-auto mx-auto"
                  style={{
                    filter: 'invert(54%) sepia(32%) saturate(727%) hue-rotate(332deg) brightness(91%) contrast(85%)'
                  }}
                />
              </div>
            </div>
          )}

          {/* Bakery-specific logo and name */}
          {bakeryName && (
            <div className="text-center mb-8">
              <div className="inline-block p-6 bg-gradient-to-br from-cream to-cream-dark rounded-3xl shadow-soft-lg mb-6">
                <img
                  src="/digibakery-logo.svg"
                  alt="Digi-Bakery"
                  className="h-12 w-auto mx-auto"
                  style={{
                    filter: 'invert(54%) sepia(32%) saturate(727%) hue-rotate(332deg) brightness(91%) contrast(85%)'
                  }}
                />
              </div>
              <h1 className="font-display text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">{bakeryName}</h1>
              <p className="font-body-serif text-charcoal-light mt-4 text-lg italic">
                {t('welcomeBack', language)}
              </p>
            </div>
          )}

          {/* Generic login title when no bakery */}
          {!bakeryName && (
            <div className="text-center mb-8">
              <h2 className="font-display text-2xl lg:text-3xl font-bold text-charcoal tracking-tight">
                {t('loginButton', language)}
              </h2>
            </div>
          )}

          {isModal && (
            <div className="bg-gradient-to-r from-terracotta/10 to-cream border-l-4 border-terracotta p-3 rounded-xl mb-4 text-left">
              <p className="font-body-serif text-sm text-charcoal">
                <strong className="font-ui">{t('cartSaved', language)}</strong> {t('cartSavedMsg', language)}
              </p>
            </div>
          )}

          {error && (
            <div className="bg-gradient-to-r from-red-50 to-cream border-l-4 border-red-600 text-charcoal px-4 py-3 rounded-xl mb-4 font-body-serif text-sm">
              {error}
            </div>
          )}

          <div className="space-y-6">
            <div onKeyPress={handleKeyPress}>
              <InputField
                label={t('email', language)}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError('');
                  setError('');
                }}
                onBlur={() => {
                  if (email) {
                    const validation = validateEmail(email);
                    if (!validation.isValid) {
                      setEmailError(validation.message);
                    }
                  }
                }}
                error={emailError}
                icon={Mail}
                placeholder="your@email.com"
                disabled={loading}
                required
                autoComplete="email"
              />
            </div>

            <div onKeyPress={handleKeyPress}>
              <PasswordField
                label={t('password', language)}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError('');
                  setError('');
                }}
                onBlur={() => {
                  if (password) {
                    const validation = validateRequired(password, 'Password');
                    if (!validation.isValid) {
                      setPasswordError(validation.message);
                    }
                  }
                }}
                error={passwordError}
                disabled={loading}
                required
                showStrengthIndicator={false}
                showRequirements={false}
                autoComplete="current-password"
              />
            </div>

            <Button
              onClick={handleLogin}
              disabled={loading}
              variant="primary" className="w-full py-4 font-bold"
            >
              {loading ? t('pleaseWait', language) : t('loginButton', language)}
            </Button>

            <div className="space-y-3">
              <Button
                onClick={() => {
                  setIsLogin(false);
                  setError('');
                }}
                disabled={loading}
                variant="ghost" className="w-full py-2 text-sm"
              >
                {t('dontHaveAccount', language) || 'Don\'t have an account? Sign up'}
              </Button>

              <button
                onClick={() => setShowForgotPassword(true)}
                disabled={loading}
                className="w-full text-sm py-2 rounded-xl text-charcoal-light hover:text-charcoal font-ui transition-colors"
              >
                {t('forgotPassword', language) || 'Forgot Password?'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
